// src/features/services/media-v2/api/mediaV2Api.ts
// ─────────────────────────────────────────────────────────────────────────────
// Media V2 — FE API layer.
// All calls go through the BFF; zero orchestrator internals leak here.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';
import { createReconnectingEventSource } from '@/utils/sseReconnect';

const BASE = '/media-v2';
const MUSIC_BASE = '/music';

// ── Types ────────────────────────────────────────────────────────────────────

export type MediaStatus = 'processing' | 'completed' | 'failed' | 'unknown';

export interface AudioEntry {
  scope_key: string;
  status: MediaStatus;
  url: string;
  title: string;
  artist: string;
  album: string;
  audio_url: string;
  filename: string;
  content_type: string;
  codec: string;
  created_at: string | null;
}

export interface AudioLibraryResponse {
  items: AudioEntry[];
  total: number;
}

export interface MusicProject {
  scope_key: string;
  status: MediaStatus;
  project_name: string;
  project_path: string;
  audio_url: string;
  audio_bpm: number | null;
  url_youtube: string;
  created_at: string | null;
}

export interface MusicProjectListResponse {
  items: MusicProject[];
  total: number;
}

export interface IngestResult {
  accepted: boolean;
  scope_key: string;
  run_id: string;
  trace_id: string;
  _stream_url: string;
  _poll_url: string;
}

export interface YouTubeIngestPayload {
  url: string;
  format?: 'opus' | 'mp3';
}

export interface CreateProjectPayload {
  project_name: string;
  url_youtube?: string;
}

export interface ReleaseProjectPayload {
  project_name: string;
  version: string;
  render_out_dir?: string;
}

export interface CloudPipelinePayload {
  project_name: string;
  url_youtube: string;
  format?: 'opus' | 'mp3' | 'wav';
  stem_mode?: '2stem' | '4stem' | '6stem';
}

export interface DeviceAttachPayload {
  scope_key: string;
  project_name: string;
}

export interface ReleaseResult {
  accepted: boolean;
  scope_key: string;
  run_id: string;
  trace_id: string;
}

export interface RunStatusResponse {
  scope_key: string;
  status: MediaStatus;
  namespace?: string;
  attributes?: Record<string, unknown>;
  created_at?: string;
}

// ── SSE Streaming Types ──────────────────────────────────────────────────

export interface MediaStreamEvent {
  stage: string;
  ts: number;
  scopeKey?: string;
  run_id?: string;
  error?: string;
  step_status?: string;
  [key: string]: unknown;
}

// ── Query Keys ───────────────────────────────────────────────────────────

export const QK_AUDIO_LIBRARY   = ['media-v2', 'audio-library'] as const;
export const QK_MUSIC_PROJECTS  = ['media-v2', 'music-projects'] as const;
export const QK_MEDIA_STATUS    = ['media-v2', 'status'] as const;

// ── API functions ────────────────────────────────────────────────────────

// YouTube Ingest
export async function ingestYoutubeApi(
  payload: YouTubeIngestPayload,
): Promise<IngestResult> {
  return bff.post<IngestResult, YouTubeIngestPayload>(`${BASE}/youtube`, payload);
}

// Audio Library
export async function fetchAudioLibraryApi(
  params?: { prefix?: string; category?: string; limit?: number },
): Promise<AudioLibraryResponse> {
  return bff.get<AudioLibraryResponse>(
    `${BASE}/audio-library`,
    params as Record<string, string>,
  );
}

export async function fetchAudioEntryApi(key: string): Promise<AudioEntry> {
  return bff.get<AudioEntry>(`${BASE}/audio-library/${encodeURIComponent(key)}`);
}

// Music Projects
export async function createMusicProjectApi(
  payload: CreateProjectPayload,
): Promise<IngestResult> {
  return bff.post<IngestResult, CreateProjectPayload>(`${BASE}/music/project`, payload);
}

export async function releaseMusicProjectApi(
  payload: ReleaseProjectPayload,
): Promise<ReleaseResult> {
  return bff.post<ReleaseResult, ReleaseProjectPayload>(`${BASE}/music/release`, payload);
}

export async function launchMusicCloudPipelineApi(
  payload: CloudPipelinePayload,
): Promise<IngestResult> {
  return bff.post<IngestResult, CloudPipelinePayload>(`${MUSIC_BASE}/pipeline/cloud`, payload);
}

export async function attachMusicDevicePipelineApi(
  payload: DeviceAttachPayload,
): Promise<IngestResult> {
  return bff.post<IngestResult, DeviceAttachPayload>(`${MUSIC_BASE}/pipeline/device/attach`, payload);
}

export async function fetchMusicProjectsApi(
  params?: { limit?: number },
): Promise<MusicProjectListResponse> {
  return bff.get<MusicProjectListResponse>(
    `${BASE}/music/projects`,
    params as Record<string, string>,
  );
}

export async function fetchMusicProjectApi(key: string): Promise<MusicProject> {
  return bff.get<MusicProject>(`${BASE}/music/projects/${encodeURIComponent(key)}`);
}

// Save / import a project directly from FE-provided data (no pipeline).
export interface ImportProjectPayload {
  project_name: string;
  scope_key?: string;
  status?: MediaStatus;
  project_path?: string;
  audio_url?: string;
  audio_bpm?: number | null;
  url_youtube?: string;
}

export async function importMusicProjectApi(
  payload: ImportProjectPayload,
): Promise<MusicProject> {
  return bff.post<MusicProject, ImportProjectPayload>(`${BASE}/music/projects/import`, payload);
}
export async function fetchRunStatusApi(key: string): Promise<RunStatusResponse> {
  return bff.get<RunStatusResponse>(`${BASE}/status/${encodeURIComponent(key)}`);
}

// ── SSE Stream ───────────────────────────────────────────────────────────

export interface MediaStreamCallbacks {
  onStage: (event: MediaStreamEvent) => void;
  onDone: () => void;
  onError?: (err: unknown) => void;
}

export function subscribeMediaStream(
  scopeKey: string,
  callbacks: MediaStreamCallbacks,
): { close: () => void } {
  const url = `/bff/media-v2/stream/${encodeURIComponent(scopeKey)}`;

  return createReconnectingEventSource(url, (es) => {
    es.addEventListener('stage', (ev: MessageEvent) => {
      try {
        callbacks.onStage(JSON.parse(ev.data));
      } catch { /* ignore parse errors */ }
    });

    es.addEventListener('done', () => {
      callbacks.onDone();
      es.close();
    });

    return false; // no auto-reconnect for one-shot pipeline streams
  });
}
